import { ReactElement, Suspense, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import { PageSpinner } from './PageSpinner';
import { useUserLanguage } from '../../hooks/useUserLanguage';

const HiddenTestId = styled.span`
  border: 0;
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

type Props = {
  title: string;
  viewportWidth?: string | number;
  viewportInitialScale?: number;
  viewportMaximumScale?: number;
  dataTestId?: string;
  children?: ReactNode;
};

export const ViewMeta = ({
  title,
  viewportWidth = 'device-width',
  viewportInitialScale = 1,
  viewportMaximumScale = 5,
  dataTestId,
  children,
}: Props): ReactElement => {
  const language = useUserLanguage();

  const titleWithIndicator =
    import.meta.env.VITE_APP_ENV === 'development' ? `[DEV] ${title}` : title;

  return (
    <>
      <Helmet
        titleTemplate={`${titleWithIndicator} %s`}
        defaultTitle={titleWithIndicator}
        htmlAttributes={{ lang: language }}
      >
        <meta
          name="viewport"
          content={`width=${viewportWidth}, initial-scale=${viewportInitialScale}, maximum-scale=${viewportMaximumScale}`}
        />
      </Helmet>
      {dataTestId && <HiddenTestId data-test-id={dataTestId} />}
      <Suspense fallback={<PageSpinner />}>{children}</Suspense>
    </>
  );
};
